import React, { useCallback, useEffect, useMemo, useState } from "react";

import orderBy from "lodash/orderBy";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import usePostDisclosure from "@fartherfinance/frontend/api/Document/hooks/usePostDisclosure";
import { DisclosureType } from "@fartherfinance/frontend/api/Document/requests/types";
import useGetFartherEmployees from "@fartherfinance/frontend/api/Entity/hooks/useFartherEmployees";
import { AdvisorId } from "@fartherfinance/frontend/api/Types";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Modal from "@src/sharedComponents/Modal/Modal";
import Dropdown, {
  AutocompleteOption,
} from "@src/sharedComponents/SAODropdown/Dropdown";
import SingleFileDropUpload from "@src/sharedComponents/SingleFileDropUpload/SingleFileDropUpload";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./DisclosureAgreementsModal.module.css";

type Option<T extends string = string> = AutocompleteOption & {
  value: T;
};

const values: Option<DisclosureType>[] = [
  { label: "Privacy Policy", value: "privacyPolicy" },
  { label: "Form ADV", value: "adv" },
  { label: "Form CRS", value: "crs" },
];

const DisclosureAgreementsModal = (): JSX.Element => {
  const [isUploading, setIsUploading] = useState(false);
  const [disclosurePdfFile, setDisclosurePdfFile] = useState<File | null>(null);
  const [disclosureType, setDisclosureType] =
    useState<Option<DisclosureType> | null>(null);
  const [disclosureAdvisor, setDisclosureAdvisor] =
    useState<Option<AdvisorId> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const auth = useAdvisorRequestAuth();
  const callPostDisclosure = usePostDisclosure(auth);
  const statusNotification = useStatusNotification();

  const advisorsQuery = useGetFartherEmployees("All", auth);

  const advisorOptions = useMemo(() => {
    return orderBy(
      advisorsQuery.data?.advisors.map((advisor) => ({
        label: `${advisor.name.first} ${advisor.name.last}`,
        value: advisor.advisorId,
      })) ?? [],
      (entity) => entity.label
    );
  }, [advisorsQuery]);

  const isAdvDisclosure = disclosureType?.value === "adv";
  const isContinueDisabled = isAdvDisclosure
    ? !disclosurePdfFile || !disclosureType || !disclosureAdvisor
    : !disclosurePdfFile || !disclosureType;

  const onClose = useCallback(
    () => navigate({ ...location, pathname: "/Advisor/Admin" }),
    [navigate, location]
  );

  const handleUploadDisclosure = async () => {
    if (!disclosurePdfFile || !disclosureType) {
      return;
    }

    setIsUploading(true);

    try {
      await callPostDisclosure({
        file: disclosurePdfFile,
        disclosureType: disclosureType.value,
        advisorId: disclosureAdvisor?.value,
      });
      setIsUploading(false);
      onClose();
      statusNotification("Disclosure Agreement Added", "Success");
    } catch {
      setIsUploading(false);
      statusNotification(
        "Something went wrong, please try again later",
        "Error"
      );
    }
  };

  useEffect(() => {
    if (!isAdvDisclosure) {
      setDisclosureAdvisor(null);
    }
  }, [isAdvDisclosure]);

  return (
    <>
      {isUploading && <LogoLoadingStill onTop />}

      <Modal closeModal={onClose} modalStyle={{ width: "620px" }}>
        <div className={styles.content}>
          <Text variant="title1">Upload a New Disclosure Agreement (PDF)</Text>

          <SingleFileDropUpload
            className={styles.upload}
            file={disclosurePdfFile}
            onChange={setDisclosurePdfFile}
            acceptedFileExtensions={[".pdf"]}
            text="Drop your file here or "
            pressText={
              <Text variant="body" className={styles.browseText}>
                browse
              </Text>
            }
          />

          <Dropdown
            formLabel="Document Type"
            value={disclosureType}
            onChange={setDisclosureType}
            values={values}
            disablePortal={false}
          />

          {isAdvDisclosure && (
            <Dropdown
              formLabel="Select Advisor"
              value={disclosureAdvisor}
              onChange={setDisclosureAdvisor}
              values={advisorOptions}
              getOptionKey={(option) => option.value}
              disablePortal={false}
              ListboxProps={{
                style: {
                  maxHeight: "30vh",
                },
              }}
            />
          )}

          <div className={styles.footer}>
            <Button
              variant="outlined"
              buttonType="primary"
              text="Cancel"
              onClick={onClose}
            />

            <Button
              variant="contained"
              buttonType="primary"
              text="Continue"
              disabled={isContinueDisabled}
              onClick={handleUploadDisclosure}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DisclosureAgreementsModal;
